import { useMemo } from 'react';
import { generateId } from '@ai-sdk/provider-utils';
import ReactMarkdown from 'react-markdown';
import { useGetSuggestions } from '@shadcn/hooks/use-suggestions';
import { chatStore } from '~/lib/stores/chat';
import { chatHooks } from '~/lib/chat-hooks';
import { menuStore } from '~/lib/stores/menu';
import { MSG_FIX_SECURITY_ISSUES } from '~/lib/constants';

import { Alert } from '~/components/ui/Alert';
import { LabelBadge } from '~/components/ui/LabelBadge';
import { Skeleton } from '~/components/ui/Skeleton';
import { Button } from '~/components/ui/Button';
import { plural } from '~/utils/plural';

export function SuggestionsManager() {
  const { data: suggestions, isLoading, error } = useGetSuggestions();

  const sortedSuggestions = useMemo(() => {
    if (!suggestions) {
      return [];
    }

    const levelOrder = { ERROR: 1, WARN: 2, INFO: 3 };

    return [...suggestions]
      .sort((a: any, b: any) => {
        const levelA = levelOrder[a.level as keyof typeof levelOrder] || 99;
        const levelB = levelOrder[b.level as keyof typeof levelOrder] || 99;

        return levelA - levelB;
      })
      .filter((suggestion) => suggestion.type === 'security');
  }, [suggestions]);

  const getBadgeVariant = (level: 'ERROR' | 'WARN' | 'INFO') => {
    switch (level) {
      case 'ERROR':
        return 'danger';
      case 'WARN':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getBadgeText = (level: 'ERROR' | 'WARN' | 'INFO') => {
    switch (level) {
      case 'ERROR':
        return 'Error';
      case 'WARN':
        return 'Warning';
      default:
        return 'Info';
    }
  };

  return (
    <div className="p-6 pt-4 lg:p-8 lg:pt-8">
      <h2 className="text-base lg:text-xl font-semibold mb-2">Security Audit</h2>
      <div className="mb-4 lg:mb-6 flex justify-between items-center">
        <p className="text-bolt-ds-textSecondary mb-4 lg:mb-6 text-sm lg:text-base">Improve your project's security.</p>
        {suggestions && suggestions.length > 0 && (
          <Button
            startIcon="i-bolt:logos-bolt-b"
            variant="secondary"
            onClick={() => {
              const message =
                MSG_FIX_SECURITY_ISSUES +
                '\n' +
                suggestions?.map((suggestion) => suggestion.title + ':' + suggestion.detail).join('\n');
              const displayMessage = `Fix ${sortedSuggestions.length} security ${plural(sortedSuggestions.length, 'issue')} identified by the audit.`;

              chatStore.promptMode.set('build');

              chatHooks.setMessages((messages) => {
                const newMessages = [...messages];

                newMessages.push({
                  role: 'user',
                  type: 'chat',
                  id: generateId(),
                  status: 'complete',
                  input: displayMessage,
                  content: message,
                  parts: [],
                });

                return newMessages;
              });

              menuStore.closeDialog();
              chatHooks.reload();
            }}
          >
            Ask Bolt to fix
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {error && (
        <Alert type="danger" className="mt-8" title="Error fetching suggestions">
          {(error as any)?.message || 'An unexpected error occurred. Please try again.'}
        </Alert>
      )}
      {suggestions && (
        <div>
          {sortedSuggestions.length > 0 ? (
            <div>
              {sortedSuggestions.map((suggestion: any) => (
                <div
                  key={suggestion.cache_key}
                  className="py-4 border-b border-bolt-elements-borderColor last:border-b-0 group relative"
                >
                  <div className="flex-1">
                    <div className="flex justify-start items-start gap-4">
                      <h4 className="font-semibold text-sm">{suggestion.title}</h4>
                      <div className="flex items-center gap-2">
                        <LabelBadge variant={getBadgeVariant(suggestion.level)} className="shrink-0">
                          {getBadgeText(suggestion.level)}
                        </LabelBadge>
                      </div>
                    </div>
                    <div className="text-sm text-bolt-ds-textSecondary mt-2 prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          code({ inline, children, ...props }: any) {
                            return inline ? (
                              <code className="bg-bolt-ds-surfaceThree px-1 rounded" {...props}>
                                {children}
                              </code>
                            ) : (
                              <pre className="bg-bolt-ds-surfaceThree p-2 rounded overflow-x-auto" {...props}>
                                <code>{children}</code>
                              </pre>
                            );
                          },
                        }}
                      >
                        {suggestion.detail}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-bolt-ds-textTertiary">Your project looks good! No suggestions at this time.</div>
          )}
        </div>
      )}
    </div>
  );
}
